import { formatDate } from "./dateFormatters";
import { 
  getSelectionCriteriaDescription, 
  isSelectionCriteriaField, 
  isSelectionCriteriaCode, 
  isCodedFieldValue 
} from "./selectionCriteriaUtils";
import { getTedCodeDescription } from "./tedCodeMappings";
import { getFieldDescription } from "./procurementFieldDescriptions";

export type TenderSection = {
  name: string;
  fields: TenderField[];
};

export type TenderField = {
  label: string;
  value: any;
  type: 'text' | 'date' | 'url' | 'boolean' | 'array' | 'multilingual' | 'coded';
  originalKey?: string; // For debugging
  codeDescription?: string; // For coded fields like procedure type, notice type, etc.
};

const getMultilingualValue = (value: any, preferredLanguage = 'eng'): string => {
  if (!value) return 'Not specified';
  
  if (Array.isArray(value) && typeof value[0] === 'string') {
    return value.join(', ');
  }
  
  if (typeof value === 'object' && !Array.isArray(value)) {
    if (value[preferredLanguage]) {
      if (Array.isArray(value[preferredLanguage])) {
        return value[preferredLanguage].join(', ');
      }
      return value[preferredLanguage];
    }
    
    const languages = Object.keys(value);
    if (languages.length > 0) {
      const firstLang = value[languages[0]];
      if (Array.isArray(firstLang)) {
        return `${firstLang.join(', ')} [${languages[0].toUpperCase()}]`;
      }
      return `${firstLang} [${languages[0].toUpperCase()}]`;
    }
  }
  
  return String(value) || 'Not specified';
};

const determineFieldType = (key: string, value: any): 'text' | 'date' | 'url' | 'boolean' | 'array' | 'multilingual' | 'coded' => {
  if (value === null || value === undefined) {
    return 'text';
  }
  
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  
  if (Array.isArray(value)) {
    return 'array';
  }
  
  // Fix: Check if value is an object before attempting to use Object.keys
  if (typeof value === 'object' && value !== null && !Array.isArray(value) && 
      Object.keys(value).some(k => ['eng', 'dan', 'deu', 'fra', 'ita', 'spa'].includes(k))) {
    return 'multilingual';
  }
  
  if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
    return 'text';
  }
  
  if (typeof value === 'string' && isCodedFieldValue(key, value)) {
    return 'coded';
  }
  
  if (
    key === 'procedure-type' || 
    key === 'PR' || 
    key === 'BT-105-Procedure' || 
    
    key === 'notice-type' || 
    key === 'BT-02-notice' || 
    
    key === 'main-activity' || 
    key === 'MA' || 
    key === 'authority-main-activity' ||
    key === 'BT-10-Procedure-Buyer' ||
    key === 'entity-main-activity' ||
    key === 'BT-610-Procedure-Buyer' ||
    
    key === 'buyer-legal-type' ||
    key === 'AA' ||
    key === 'BT-11-Procedure-Buyer' ||
    
    key === 'contract-nature' ||
    key === 'NC' ||
    key.includes('contract-nature-add') ||
    key.startsWith('BT-531') ||
    
    key === 'corporate-body' ||
    
    key === 'funding' ||
    
    key === 'submission-language' ||
    key === 'SUB_LG' || 
    key === 'BT-97-Lot' ||
    
    key.includes('strategic-procurement') ||
    key === 'BT-06-Lot' ||
    
    key.includes('environmental-impact') ||
    key === 'BT-774-Lot' ||
    
    key.includes('document-restricted') ||
    key === 'BT-14-Lot' ||
    key === 'BT-14-Part' ||
    
    key.includes('contract-conditions-code') ||
    key === 'OPP-030-Tender' ||
    
    key.includes('subcontracting-obligation') ||
    key === 'BT-65-Lot' ||

    key === 'exclusion-grounds' ||
    key === 'BT-67(a)-Procedure' ||
    
    key.includes('tenderer-legal-form') ||
    key === 'BT-761-Lot' ||
    
    key.includes('guarantee-required') ||
    key === 'BT-751-Lot' ||
    
    key.includes('non-disclosure-agreement') ||
    key === 'BT-801-Lot' ||

    (typeof value === 'string' && isSelectionCriteriaCode(value))
  ) {
    return 'coded';
  }
  
  if (
    key.includes('date') || 
    key.includes('-time') ||
    key.includes('deadline') || 
    key.endsWith('-at')
  ) {
    return 'date';
  }
  
  if (
    (
      key.includes('url') || 
      key.includes('link') || 
      key.includes('website') ||
      key.includes('internet-address') ||
      (key.includes('gateway') && typeof key === 'string' && key.includes && !key.includes('ted'))
    ) 
  ) {
    return 'url';
  }
  
  return 'text';
};

const getCodeDescription = (key: string, value: any): string | undefined => {
  if (!value) return undefined;
  
  const tedDescription = getTedCodeDescription(key, String(value));
  if (tedDescription) {
    return tedDescription;
  }
  
  if (typeof value === 'string' && isSelectionCriteriaCode(value)) {
    return getSelectionCriteriaDescription(value);
  }
  
  if (typeof value === 'string' && isCodedFieldValue(key, value)) {
    return getSelectionCriteriaDescription(value);
  }
  
  return getFieldDescription(key, String(value));
};

const formatFieldValue = (value: any, type: 'text' | 'date' | 'url' | 'boolean' | 'array' | 'multilingual' | 'coded', key?: string): any => {
  if (value === null || value === undefined) {
    return 'Not specified';
  }
  
  switch (type) {
    case 'boolean':
      return value === true ? 'Yes' : 'No';
      
    case 'date':
      if (typeof value === 'string') {
        return formatDate(value);
      }
      return String(value);
      
    case 'multilingual':
      return getMultilingualValue(value);
    
    case 'coded':
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return String(value);
      
    case 'array':
      if (Array.isArray(value)) {
        if (value.length === 0) return 'None';
        return value.map(item => {
          if (typeof item === 'object' && item !== null) {
            return JSON.stringify(item);
          }
          return String(item);
        });
      }
      return String(value);
      
    default:
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
      }
      return String(value);
  }
};

const keyToLabel = (key: string): string => {
  if (!key || typeof key !== 'string') {
    return 'Unknown Field';
  }

  if (isSelectionCriteriaField(key)) {
    const matches = key.match(/slc-[a-z-]+|ef-stand|sui-act|tp-abil/);
    if (matches && matches[0]) {
      return getSelectionCriteriaDescription(matches[0]);
    }
    
    if (key.includes('selection-criterion')) {
      return 'Selection Criteria: ' + key
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
        .replace('Selection Criterion', '');
    }
  }
  
  // Location fields handling
  if (key === 'place-of-performance-subdiv-lot' || 
      key === 'buyer-country-sub' || 
      key === 'BT-5071-Lot') {
    return 'Delivery Area (NUTS)';
  }
  
  if (key === 'PR' || key === 'BT-105-Procedure') {
    return 'Procedure Type';
  }
  
  if (key === 'MA') {
    return 'Main Activity';
  }
  
  if (key === 'AA' || key === 'BT-11-Procedure-Buyer') {
    return 'Buyer Legal Type';
  }
  
  if (key === 'NC') {
    return 'Contract Nature';
  }
  
  if (key === 'CY') {
    return 'Country';
  }
  
  if (key === 'RC') {
    return 'Country';
  }
  
  if (key === 'buyer-country') {
    return 'Country';
  }
  
  if (key === 'place-of-performance-country-lot') {
    return 'Performance Country';
  }
  
  if (key === 'AU') {
    return 'Official Name';
  }
  
  if (key === 'BI') {
    return 'Registration Number';
  }
  
  if (key === 'TW') {
    return 'Town';
  }
  
  if (key === 'DT') {
    return 'Deadline for Receipt of Tenders';
  }
  
  if (key === 'DD') {
    return 'Deadline for Additional Information';
  }
  
  if (key === 'PD' || key === 'OPP-012-notice') {
    return 'Publication Date';
  }
  
  if (key === 'OJ' || key === 'OPP-011-notice') {
    return 'OJ S Issue Number';
  }
  
  if (key === 'TV') {
    return 'Total Value';
  }
  
  if (key === 'TV_CUR') {
    return 'Currency';
  }
  
  if (key === 'SUB_LG' || key === 'BT-97-Lot') {
    return 'Submission Languages';
  }
  
  if (key === 'BT-06-Lot' || key.includes('strategic-procurement')) {
    return 'Strategic Procurement Aim';
  }
  
  if (key === 'BT-774-Lot' || key.includes('environmental-impact')) {
    return 'Environmental Impact Approach';
  }
  
  if (key === 'BT-14-Lot' || key === 'BT-14-Part' || key.includes('document-restricted')) {
    return 'Document Access Restriction';
  }
  
  if (key === 'BT-65-Lot' || key.includes('subcontracting-obligation')) {
    return 'Subcontracting Obligations';
  }
  
  if (key === 'BT-10-Procedure-Buyer' || key === 'authority-main-activity') {
    return 'Authority Main Activity';
  }
  
  if (key === 'BT-610-Procedure-Buyer' || key === 'entity-main-activity') {
    return 'Entity Main Activity';
  }
  
  if (key === 'BT-67(a)-Procedure' || key === 'exclusion-grounds') {
    return 'Exclusion Grounds';
  }
  
  if (key.startsWith('BT-')) {
    const parts = key.split('-');
    if (parts.length > 2) {
      const fieldName = parts.slice(2).join(' ');
      return `${fieldName} (${parts[0]}-${parts[1]})`;
    }
    return key;
  }
  
  return key
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const shouldFilterField = (key: string, value: any): boolean => {
  if (!key || typeof key !== 'string') {
    return true;
  }

  if (key === 'links' || (typeof key === 'string' && (key.includes('ted') || key.includes('TED')))) {
    return true;
  }
  
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  
  if (value !== null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
    return true;
  }
  
  if (typeof value === 'string' && (value.includes('ted.europa.eu') || value.includes('simap.ted.europa.eu'))) {
    return true;
  }
  
  return false;
};

const processedFieldKeys = new Set<string>();
const getFieldIdentifier = (key: string, value: any): string => {
  if (!key || typeof key !== 'string') {
    return String(Math.random());
  }

  if ((key.includes('date') || key.includes('-time') || key.includes('deadline')) && value) {
    return `${key}-${value}`;
  }
  return key;
};

export const transformTenderData = (tenderData: any): TenderSection[] => {
  if (!tenderData || Object.keys(tenderData).length === 0) {
    return [];
  }
  
  const sections: Record<string, TenderField[]> = {
    "Basic Information": [],
    "Buyer Information": [],
    "Procedure Details": [],
    "Selection Criteria": [],
    "Lot and Award Information": [],
    "Submission & Document Links": [],
    "Deadlines & Timing": [],
    "Classification & CPV Codes": [],
    "Place of Performance": [],
    "Value & Financial Details": [],
    "Environmental & Social": [],
    "Legal & Regulatory Information": [],
    "Additional Information": []
  };
  
  processedFieldKeys.clear();
  
  Object.entries(tenderData).forEach(([key, value]) => {
    // Skip if key or value is invalid
    if (!key || key === null || key === undefined) {
      return;
    }

    if (shouldFilterField(key, value)) {
      return;
    }
    
    const fieldId = getFieldIdentifier(key, value);
    
    if (processedFieldKeys.has(fieldId)) {
      return;
    }
    
    processedFieldKeys.add(fieldId);
    
    let normalizedKey = key;
    if (key === 'PD' || key === 'OPP-012-notice') {
      normalizedKey = 'publication-date';
    } else if (key === 'DT') {
      normalizedKey = 'deadline-receipt-tender-date-lot';
    } else if (key === 'DD') {
      normalizedKey = 'deadline-receipt-request';
    }
    
    const type = determineFieldType(key, value);
    
    const formattedValue = formatFieldValue(value, type, key);
    
    const codeDescription = type === 'coded' ? getCodeDescription(key, value) : undefined;
    
    const field: TenderField = {
      label: keyToLabel(key),
      value: formattedValue,
      type,
      originalKey: key,
      codeDescription
    };
    
    if (isSelectionCriteriaField(key)) {
      sections["Selection Criteria"].push(field);
      return;
    }
    
    // Check that key exists and is a string before checking includes
    if (typeof key !== 'string') {
      sections["Additional Information"].push(field);
      return;
    }
    
    if (
      key === 'publication-number' ||
      key === 'notice-title' ||
      key === 'title' ||
      key === 'announcement-title' ||
      key === 'ojs-number' ||
      key === 'OJ' ||
      key === 'OPP-011-notice' ||
      key === 'issue-date' ||
      key === 'publication-date' ||
      key === 'PD' ||
      key === 'OPP-012-notice' ||
      key === 'dispatch-date' ||
      key === 'notice-type' ||
      key === 'notice-identifier' ||
      key === 'notice-version' ||
      key === 'notice-subtype' ||
      key === 'form-type' ||
      key === 'BT-24-Procedure'
    ) {
      sections["Basic Information"].push(field);
    } 
    else if (
      key.includes('buyer') ||
      key === 'AU' ||
      key.includes('organisation-name') ||
      key.includes('buyer-name') ||
      key.includes('buyer-city') ||
      key === 'TW' ||
      key.includes('buyer-country') ||
      key === 'CY' ||
      key.includes('buyer-email') ||
      key.includes('buyer-internet-address') ||
      key.includes('buyer-identifier') ||
      key === 'BI' ||
      key.includes('buyer-post-code') ||
      key.includes('buyer-tel') ||
      key.includes('buyer-fax') ||
      key.includes('buyer-contact-point') ||
      key.includes('main-activity') ||
      key === 'MA' ||
      key.includes('buyer-legal-type') ||
      key === 'AA' ||
      key === 'BT-11-Procedure-Buyer' ||
      key.includes('buyer-contracting-entity') ||
      key === 'corporate-body' ||
      key === 'authority-main-activity' ||
      key === 'BT-10-Procedure-Buyer' ||
      key === 'entity-main-activity' ||
      key === 'BT-610-Procedure-Buyer'
    ) {
      sections["Buyer Information"].push(field);
    }
    else if (
      key.includes('procedure-type') ||
      key === 'PR' || 
      key === 'BT-105-Procedure' ||
      key.includes('procedure-accelerated') ||
      key.includes('procedure-features') ||
      key.includes('procedure-identifier') ||
      key.includes('procedure-justification') ||
      key.includes('internal-identifier-proc') ||
      key === 'BT-01-procedure' ||
      (key.startsWith('BT-') && key.includes('Procedure')) ||
      key.includes('competitive-procedure') ||
      key.includes('negotiated-procedure') ||
      key.includes('award-justification') ||
      key.includes('direct-award')
    ) {
      sections["Procedure Details"].push(field);
    }
    else if (
      key.includes('lot') &&
      !key.includes('place-of-performance') ||
      key.includes('award-criterion') ||
      key.includes('award-criteria') ||
      key.includes('contract-nature') ||
      key === 'NC' ||
      key.includes('contract-conditions') ||
      key === 'BT-24-Lot' ||
      key.includes('tender-lot-identifier') ||
      key.includes('identifier-lot') ||
      key.includes('title-lot') ||
      key.includes('description-lot') ||
      key.startsWith('BT-531') ||
      key.includes('contract-nature-add')
    ) {
      sections["Lot and Award Information"].push(field);
    }
    else if (
      key.includes('submission') ||
      key === 'SUB_LG' ||
      key === 'BT-97-Lot' ||
      key.includes('document') &&
      !key.includes('document-restricted') ||
      key.includes('url') ||
      key.includes('electronic') ||
      key.includes('esender') ||
      key.includes('language') ||
      key.includes('document-restricted') ||
      key === 'BT-14-Lot' ||
      key === 'BT-14-Part'
    ) {
      sections["Submission & Document Links"].push(field);
    }
    else if (
      key.includes('date') ||
      key.includes('time') ||
      key.includes('deadline') ||
      key === 'DT' ||
      key === 'DD' ||
      key.includes('receipt') ||
      key.includes('duration') ||
      key.includes('start-date') ||
      key.includes('end-date') ||
      key.includes('contract-duration') ||
      key.includes('renewal') ||
      key.includes('recurrence')
    ) {
      sections["Deadlines & Timing"].push(field);
    }
    else if (
      key.includes('cpv') ||
      key.includes('classification') ||
      key.includes('category') ||
      key.includes('main-classification')
    ) {
      sections["Classification & CPV Codes"].push(field);
    }
    else if (
      key.includes('place-of-performance') ||
      key.includes('buyer-country-sub') ||
      key.includes('BT-5071-Lot') ||
      key.includes('nuts') ||
      key.includes('country') ||
      key === 'RC' ||
      key.includes('location') ||
      key.includes('region')
    ) {
      sections["Place of Performance"].push(field);
    }
    else if (
      key.includes('value') ||
      key === 'TV' ||
      key.includes('price') ||
      key.includes('cost') ||
      key.includes('euro') ||
      key.includes('currency') ||
      key === 'TV_CUR' ||
      key.includes('estimated-value') ||
      key.includes('tender-value') ||
      key.includes('total-value') ||
      key.includes('fund')
    ) {
      sections["Value & Financial Details"].push(field);
    }
    else if (
      key.includes('environmental') ||
      key.includes('social') ||
      key.includes('strategic-procurement') ||
      key === 'BT-06-Lot' ||
      key.includes('environmental-impact') ||
      key === 'BT-774-Lot' ||
      key.includes('sustainable') ||
      key.includes('green')
    ) {
      sections["Environmental & Social"].push(field);
    }
    else if (
      key.includes('legal') ||
      key.includes('regulatory') ||
      key.includes('law') ||
      key.includes('regulation') ||
      key.includes('exclusion') ||
      key === 'BT-67(a)-Procedure' ||
      key.includes('udelukkelse') ||
      key.includes('legislation') ||
      key.includes('subcontracting-obligation') ||
      key === 'BT-65-Lot'
    ) {
      sections["Legal & Regulatory Information"].push(field);
    }
    else {
      sections["Additional Information"].push(field);
    }
  });
  
  return Object.entries(sections)
    .filter(([_, fields]) => fields.length > 0)
    .map(([name, fields]) => ({
      name,
      fields
    }));
};
